import { useQueryClient } from '@tanstack/react-query';

import { useToastStore } from '@tankef/core-react';
import { useSound } from '@tankef/core-react';
import { useTranslation } from '@tankef/i18n';
import {
  useKycStatusUpdates,
  useBalanceUpdates,
  usePaymentStatusUpdates,
  useNotificationUpdates,
  useSystemAlerts,
  type KycStatusUpdate,
  type BalanceUpdate,
  type PaymentStatusUpdate,
  type NotificationUpdate,
  type SystemAlertUpdate,
} from '@tankef/websocket';

import { invalidateByEvent } from './realtimeInvalidations';

import { useParty } from '@/domains/identity/auth/api';
import { usePrimaryAccount } from '@/domains/wallet/accounts/api';

function formatCents(cents: number): string {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(cents / 100);
}

/**
 * Subscribes globally to all WebSocket event channels.
 *
 * Invalidation strategy: each event type maps to a set of queryKey prefixes
 * in realtimeInvalidations.ts. React Query only network-fetches queries with
 * active observers, so components on other routes just get marked stale and
 * refetch lazily — no wasted requests.
 *
 * Toast logic stays here (needs typed access to event payload fields).
 * Query invalidation logic lives in realtimeInvalidations.ts (declarative map).
 *
 * Mount once inside DashboardLayout (after WebSocket connection is acquired).
 */
export function RealtimeGateProvider() {
  const { t } = useTranslation('notifications');
  const queryClient = useQueryClient();
  const addToast = useToastStore(s => s.addToast);
  const party = useParty();
  const { data: account } = usePrimaryAccount();

  const sound = useSound();
  const partyId = party?.id;
  const accountId = account?.id;

  // ── Balance / Account events ───────────────────────────────────────────────
  useBalanceUpdates(accountId, (update: BalanceUpdate) => {
    invalidateByEvent(queryClient, update.type);

    if (update.type === 'deposit_received' && update.payload.amount_cents) {
      sound.play('financial.deposit');
      addToast({
        type: 'success',
        title: t('realtime.balance.depositTitle'),
        message: t('realtime.balance.depositMessage', {
          amount: formatCents(update.payload.amount_cents),
        }),
        duration: 8000,
      });
    }

    if (update.type === 'withdrawal_completed' && update.payload.amount_cents) {
      addToast({
        type: 'info',
        title: t('realtime.balance.withdrawalTitle'),
        message: t('realtime.balance.withdrawalMessage', {
          amount: formatCents(update.payload.amount_cents),
        }),
        duration: 8000,
      });
    }

    if (update.type === 'account_updated' && !account?.clabe) {
      addToast({
        type: 'success',
        title: t('realtime.account.clabeActivatedTitle'),
        message: t('realtime.account.clabeActivatedMessage'),
        duration: 10000,
      });
    }
  });

  // ── Payment events ─────────────────────────────────────────────────────────
  usePaymentStatusUpdates((update: PaymentStatusUpdate) => {
    invalidateByEvent(queryClient, update.type);

    if (update.type === 'payment_failed' && update.failure_reason) {
      addToast({
        type: 'error',
        title: t('realtime.payment.failedTitle'),
        message: update.failure_reason,
        duration: 8000,
      });
    }

    if (update.type === 'payment_completed') {
      sound.play('financial.deposit');
      addToast({
        type: 'success',
        title: t('realtime.payment.completedTitle'),
        message: t('realtime.payment.completedMessage', {
          amount: formatCents(update.amount_cents),
        }),
        duration: 8000,
      });
    }
  });

  // ── KYC events ─────────────────────────────────────────────────────────────
  useKycStatusUpdates(partyId, (update: KycStatusUpdate) => {
    invalidateByEvent(queryClient, update.type);

    if (update.type === 'kyc_approved') {
      sound.play('status.success');
      addToast({
        type: 'success',
        title: t('realtime.kyc.approvedTitle'),
        message: t('realtime.kyc.approvedMessage'),
        duration: 8000,
      });
    }

    if (update.type === 'kyc_level_upgraded') {
      const limits = update.payload.new_limits;
      const limitsMsg = limits
        ? t('realtime.kyc.upgradedWithLimits', {
            daily: formatCents(limits.daily_cents),
            monthly: formatCents(limits.monthly_cents),
          })
        : t('realtime.kyc.upgradedMessage');
      addToast({
        type: 'success',
        title: t('realtime.kyc.upgradedTitle'),
        message: limitsMsg,
        duration: 8000,
      });
    }

    if (update.type === 'kyc_rejected') {
      addToast({
        type: 'error',
        title: t('realtime.kyc.rejectedTitle'),
        message: update.payload.rejection_reason ?? t('realtime.kyc.rejectedMessage'),
        duration: 10000,
      });
    }
  });

  // ── Notifications events ───────────────────────────────────────────────────
  // Eventos del NotificationsChannel (new_notification, unread_count_updated,
  // notification_read, batch_notifications). Solo invalida; el badge del icono
  // de notificaciones consume la query.
  useNotificationUpdates((update: NotificationUpdate) => {
    invalidateByEvent(queryClient, update.type);

    if (update.type === 'new_notification' && update.payload?.notification) {
      const { title, priority, mandatory } = update.payload.notification;
      if (mandatory || priority === 'high' || priority === 'urgent') {
        sound.play('status.alert');
        addToast({
          type: priority === 'urgent' ? 'error' : 'info',
          title,
          duration: 10000,
        });
      }
    }
  });

  // ── System alerts (tenant-wide) ────────────────────────────────────────────
  // Banners persistentes para incidentes a nivel sistema: SPEI caído,
  // circuit breakers abiertos, mantenimiento programado, etc.
  useSystemAlerts((update: SystemAlertUpdate) => {
    invalidateByEvent(queryClient, update.type);

    const { severity, title, message } = update.payload;
    addToast({
      type: severity === 'critical' ? 'error' : severity === 'warning' ? 'warning' : 'info',
      title,
      message,
      duration: severity === 'critical' ? 0 : 15000, // 0 = sticky hasta cierre manual
    });
  });

  return null;
}
